import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle,
  FileText,
  Loader2,
  SkipForward,
  Upload,
  X,
} from "lucide-react";
import { candidateAPI, uploadAPI } from "../api/client";
import { formatApiErrorDetail } from "../utils/apiError";

const emptyReviewForm = {
  name: "",
  phone: "",
  email: "",
  skills: "",
  gender: "",
  current_city: "",
  jd_id: "",
  source: "Bulk CV Upload",
  role: "",
  experience: "",
  current_company: "",
  expected_salary: "",
  notes: "",
  location: "",
  status: "Active",
};

const activeItemStatuses = ["ready_for_review", "approval_failed"];

const firstActionableItem = (batch) => (
  batch?.items?.find((item) => activeItemStatuses.includes(item.status)) || null
);

function UploadPage() {
  const [mode, setMode] = useState("data");

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const [cvFiles, setCvFiles] = useState([]);
  const [cvError, setCvError] = useState("");
  const [cvBatch, setCvBatch] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [activeItemId, setActiveItemId] = useState(null);
  const [reviewForm, setReviewForm] = useState(emptyReviewForm);
  const [reviewErrors, setReviewErrors] = useState({});
  const [approving, setApproving] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  const activeItem = useMemo(() => {
    if (!cvBatch?.items?.length) return null;
    return cvBatch.items.find((item) => item.id === activeItemId) || null;
  }, [cvBatch, activeItemId]);

  useEffect(() => {
    if (!cvBatch?.items?.length || activeItemId) return;
    const next = firstActionableItem(cvBatch);
    if (next) setActiveItemId(next.id);
  }, [cvBatch, activeItemId]);

  useEffect(() => {
    let cancelled = false;

    const restoreLatestActiveBatch = async () => {
      try {
        const response = await uploadAPI.getLatestActiveCvBatch();
        let batch = response.data.batch;
        if (!batch || cancelled) return;

        setMode("cv");
        setCvBatch(batch);
        const next = firstActionableItem(batch);
        setActiveItemId(next?.id || null);

        if ((batch.summary?.uploaded || 0) > 0) {
          await processBatchUntilReady(batch.id, batch);
        }
      } catch {
        // Recovery is best-effort; upload page remains usable for new batches.
      }
    };

    restoreLatestActiveBatch();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!activeItem) return;
    const parsed = activeItem.parsed_data || {};
    setReviewForm({
      ...emptyReviewForm,
      ...parsed,
      phone: parsed.phone ? String(parsed.phone).replace(/\D/g, "").slice(-10) : "",
      experience: parsed.experience != null ? String(parsed.experience) : "",
      source: parsed.source || "Bulk CV Upload",
      status: "Active",
    });
    setReviewErrors(activeItem.validation_errors || {});
    setDuplicateWarning(activeItem.duplicate_snapshot || null);
  }, [activeItemId]);

  useEffect(() => {
    const phoneDigits = reviewForm.phone.replace(/\D/g, "");
    if (phoneDigits.length !== 10) {
      setDuplicateWarning(activeItem?.duplicate_snapshot || null);
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        const duplicate = await candidateAPI.checkDuplicate({ phone: phoneDigits });
        setDuplicateWarning(duplicate.data.exists ? duplicate.data.candidate : null);
      } catch {
        setDuplicateWarning(activeItem?.duplicate_snapshot || null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [reviewForm.phone, activeItem]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const ext = selectedFile.name.split(".").pop().toLowerCase();
    if (!["csv", "xlsx"].includes(ext)) {
      setError("Only CSV and XLSX files are allowed");
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setError("");
    setResult(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setError("Please select a file");
      return;
    }

    setUploading(true);
    setError("");
    setResult(null);

    try {
      const response = await uploadAPI.bulkUpload(file);
      setResult(response.data);
      setFile(null);
      const fileInput = document.querySelector("#file-upload");
      if (fileInput) fileInput.value = "";
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail, "Upload failed."));
    } finally {
      setUploading(false);
    }
  };

  const validateCvFiles = (files) => {
    const accepted = [];
    const messages = [];
    files.forEach((selected) => {
      const ext = selected.name.split(".").pop().toLowerCase();
      if (!["pdf", "docx"].includes(ext)) {
        messages.push(`${selected.name}: only PDF and DOCX files are allowed.`);
        return;
      }
      if (selected.size > 5 * 1024 * 1024) {
        messages.push(`${selected.name}: file size exceeds 5 MB.`);
        return;
      }
      accepted.push(selected);
    });
    return { accepted, messages };
  };

  const handleCvFileChange = (event) => {
    const selected = Array.from(event.target.files || []);
    const { accepted, messages } = validateCvFiles(selected);
    const combined = [...cvFiles, ...accepted].slice(0, 10);

    if (cvFiles.length + accepted.length > 10) {
      messages.push("A batch can contain no more than 10 CV files.");
    }

    setCvFiles(combined);
    setCvError(messages.join(" "));
    setCvBatch(null);
    setActiveItemId(null);
    event.target.value = "";
  };

  const processBatchUntilReady = async (batchId, initialBatch) => {
    let current = initialBatch;
    setProcessing(true);
    try {
      while ((current.summary?.uploaded || 0) > 0) {
        const response = await uploadAPI.processNextCv(batchId);
        current = response.data.batch;
        setCvBatch(current);
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleCvUpload = async () => {
    if (cvFiles.length === 0) {
      setCvError("Please select at least one CV file.");
      return;
    }

    setCvError("");
    setProcessing(true);
    setCvBatch(null);
    setActiveItemId(null);

    try {
      const response = await uploadAPI.createCvBatch(cvFiles);
      const batch = response.data.batch;
      setCvBatch(batch);
      setCvFiles([]);
      await processBatchUntilReady(batch.id, batch);
    } catch (err) {
      setCvError(formatApiErrorDetail(err.response?.data?.detail, "Bulk CV upload failed."));
    } finally {
      setProcessing(false);
    }
  };

  const validateReviewForm = () => {
    const nextErrors = {};
    const phoneDigits = reviewForm.phone.replace(/\D/g, "");
    if (!reviewForm.name.trim()) nextErrors.name = "Name is required.";
    if (reviewForm.name.trim() && reviewForm.name.trim().length < 2) nextErrors.name = "Name must be at least 2 characters.";
    if (!phoneDigits) nextErrors.phone = "Phone number is required.";
    if (phoneDigits && phoneDigits.length !== 10) nextErrors.phone = "Phone number must be exactly 10 digits.";
    if (!reviewForm.email.trim()) nextErrors.email = "Email is required.";
    if (reviewForm.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reviewForm.email.trim())) {
      nextErrors.email = "Please enter a valid email address.";
    }
    if (reviewForm.skills.split(/[,;|\n]+/).filter((skill) => skill.trim()).length === 0) {
      nextErrors.skills = "At least one skill is required.";
    }
    setReviewErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const updateReviewField = (field, value) => {
    setReviewForm((previous) => ({ ...previous, [field]: value }));
    setReviewErrors((previous) => {
      const next = { ...previous };
      delete next[field];
      return next;
    });
  };

  const refreshBatch = async (batchId) => {
    const response = await uploadAPI.getCvBatch(batchId);
    setCvBatch(response.data.batch);
    return response.data.batch;
  };

  const handleApprove = async () => {
    if (!activeItem || approving) return;
    if (duplicateWarning) {
      setCvError("Candidate already exists. Approval is blocked for exact phone or email duplicates.");
      return;
    }
    if (!validateReviewForm()) {
      setCvError("Please complete all required candidate fields.");
      return;
    }

    setApproving(true);
    setCvError("");
    try {
      const payload = {
        ...reviewForm,
        phone: reviewForm.phone.replace(/\D/g, ""),
        jd_id: reviewForm.jd_id || null,
      };
      const response = await uploadAPI.approveCvItem(activeItem.id, payload);
      const batch = response.data.batch;
      setCvBatch(batch);
      const next = firstActionableItem(batch);
      setActiveItemId(next?.id || activeItem.id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && !Array.isArray(detail)) {
        if (detail.candidate) setDuplicateWarning(detail.candidate);
        const fieldErrors = detail.fields || detail;
        if (!detail.message) setReviewErrors(fieldErrors);
      }
      setCvError(formatApiErrorDetail(detail, "Approval failed."));
      if (cvBatch?.id) await refreshBatch(cvBatch.id);
    } finally {
      setApproving(false);
    }
  };

  const handleSkip = async () => {
    if (!activeItem) return;
    setCvError("");
    try {
      const response = await uploadAPI.skipCvItem(activeItem.id);
      const batch = response.data.batch;
      setCvBatch(batch);
      const next = firstActionableItem(batch);
      setActiveItemId(next?.id || null);
    } catch (err) {
      setCvError(formatApiErrorDetail(err.response?.data?.detail, "Skip failed."));
    }
  };

  const removeCvFile = (index) => {
    setCvFiles((previous) => previous.filter((_, itemIndex) => itemIndex !== index));
  };

  const renderDataUpload = () => (
    <>
      <div className="bg-navy-light rounded-lg shadow-lg border border-teal/30 p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-textLight mb-2">Select File</label>
            <div className="border-2 border-dashed border-teal/30 rounded-lg p-8 text-center hover:border-teal transition-all">
              <input type="file" accept=".csv,.xlsx" onChange={handleFileChange} className="hidden" id="file-upload" />
              <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center">
                <Upload size={48} className="text-teal mb-4" />
                {file ? (
                  <>
                    <p className="text-textLight font-medium">{file.name}</p>
                    <p className="text-sm text-textLight/70 mt-1">{(file.size / 1024).toFixed(2)} KB</p>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.preventDefault();
                        setFile(null);
                        document.getElementById("file-upload").value = "";
                      }}
                      className="mt-3 text-sm text-gold hover:text-gold-light transition-colors"
                    >
                      Remove file
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-textLight font-medium">Click to upload or drag and drop</p>
                    <p className="text-sm text-textLight/70 mt-1">CSV or XLSX files (max 10MB)</p>
                  </>
                )}
              </label>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-4 bg-gold/20 border border-gold/50 rounded-lg text-gold">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={!file || uploading}
            className="w-full flex items-center justify-center px-6 py-3 bg-teal text-white rounded-lg hover:bg-teal-dark font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-teal/30"
          >
            {uploading ? <Loader2 size={20} className="mr-2 animate-spin" /> : <Upload size={20} className="mr-2" />}
            {uploading ? "Uploading..." : "Upload Candidates"}
          </button>
        </form>
      </div>

      {result && (() => {
        const hardErrors = result.errors || [];
        const warningMessages = result.warnings || [];
        const hasHardErrors = hardErrors.length > 0 || (result.rows_skipped || 0) > 0;
        const hasWarnings = warningMessages.length > 0;
        const statusTone = hasHardErrors ? "text-gold" : "text-teal";

        return (
        <div className="bg-navy-light border border-teal/30 rounded-lg p-6 shadow-lg">
          <div className="flex items-center gap-2 mb-4">
            {hasHardErrors ? (
              <AlertCircle size={24} className="text-gold" />
            ) : (
              <CheckCircle size={24} className="text-teal" />
            )}
            <h3 className={`text-lg font-heading font-semibold ${statusTone}`}>{result.message}</h3>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <SummaryCard label="Total Rows" value={result.rows_total} />
            <SummaryCard label="Added" value={result.rows_added} tone="teal" />
            <SummaryCard label="Updated" value={result.rows_updated} tone="tealLight" />
            <SummaryCard label="Skipped" value={result.rows_skipped} tone="gold" />
          </div>

          {hardErrors.length > 0 && (
            <div className="bg-gold/20 border border-gold/50 rounded-lg p-4 mb-4">
              <h4 className="font-heading font-semibold text-gold mb-2">Rows skipped:</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-textLight/80">
                {hardErrors.map((err, idx) => <li key={idx}>{err}</li>)}
              </ul>
            </div>
          )}

          {hasWarnings && (
            <div className="bg-navy border border-teal/30 rounded-lg p-4 mb-4">
              <h4 className="font-heading font-semibold text-teal mb-2">Imported with warnings:</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-textLight/80">
                {warningMessages.map((warning, idx) => <li key={idx}>{warning}</li>)}
              </ul>
            </div>
          )}

          <Link to="/candidates" className="inline-flex items-center px-4 py-2 bg-teal text-white rounded-lg hover:bg-teal-dark font-medium transition-all shadow-lg shadow-teal/30">
            View Candidates
            <ArrowRight size={18} className="ml-2" />
          </Link>
        </div>
        );
      })()}

      <div className="bg-navy-light border border-teal/30 rounded-lg p-6 shadow-lg">
        <h3 className="font-heading font-semibold text-teal mb-3 flex items-center">
          <FileText size={20} className="mr-2" />
          File Format Instructions
        </h3>
        <div className="text-sm text-textLight/80 space-y-2">
          <p><strong className="text-gold">Required columns:</strong> name and at least one valid phone or email</p>
          <p><strong className="text-gold">Optional columns:</strong> skills, role, experience, location</p>
          <pre className="bg-navy p-3 rounded border border-teal/20 text-xs overflow-x-auto text-textLight">
{`name,phone,email,skills,role,experience,location
Jane Doe,9876543210,jane@example.com,"Manual testing, Selenium",QA Engineer,2 years,Mumbai
John Smith,9876543211,john@example.com,"Recruitment, Screening",,0 years,Pune`}
          </pre>
        </div>
      </div>
    </>
  );

  const renderCvUpload = () => (
    <>
      <div className="bg-navy-light rounded-lg shadow-lg border border-teal/30 p-8 space-y-6">
        <div>
          <label className="block text-sm font-medium text-textLight mb-2">Select CV Files</label>
          <div className="border-2 border-dashed border-teal/30 rounded-lg p-8 text-center hover:border-teal transition-all">
            <input type="file" accept=".pdf,.docx" multiple onChange={handleCvFileChange} className="hidden" id="cv-file-upload" />
            <label htmlFor="cv-file-upload" className="cursor-pointer flex flex-col items-center">
              <Upload size={48} className="text-teal mb-4" />
              <p className="text-textLight font-medium">Click to upload or drag and drop</p>
              <p className="text-sm text-textLight/70 mt-1">PDF or DOCX files, up to 10 CVs, 5 MB each</p>
            </label>
          </div>
        </div>

        {cvFiles.length > 0 && (
          <div className="space-y-2">
            {cvFiles.map((selected, index) => (
              <div key={`${selected.name}-${index}`} className="flex items-center justify-between rounded-lg border border-teal/20 bg-navy p-3">
                <div>
                  <p className="text-sm font-medium text-textLight">{selected.name}</p>
                  <p className="text-xs text-textLight/60">{(selected.size / 1024).toFixed(2)} KB</p>
                </div>
                <button type="button" onClick={() => removeCvFile(index)} className="text-textLight/60 hover:text-gold" aria-label="Remove CV">
                  <X size={18} />
                </button>
              </div>
            ))}
          </div>
        )}

        {cvError && (
          <div className="flex items-start gap-2 p-4 bg-gold/20 border border-gold/50 rounded-lg text-gold">
            <AlertCircle size={20} className="mt-0.5 flex-shrink-0" />
            <span>{cvError}</span>
          </div>
        )}

        <button
          type="button"
          disabled={cvFiles.length === 0 || processing}
          onClick={handleCvUpload}
          className="w-full flex items-center justify-center px-6 py-3 bg-teal text-white rounded-lg hover:bg-teal-dark font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-teal/30"
        >
          {processing ? <Loader2 size={20} className="mr-2 animate-spin" /> : <Upload size={20} className="mr-2" />}
          {processing ? "Processing CVs..." : "Upload and Parse CVs"}
        </button>
      </div>

      {cvBatch && (
        <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-6">
          <div className="bg-navy-light border border-teal/30 rounded-lg p-5 shadow-lg space-y-4">
            <div>
              <h3 className="font-heading text-lg font-semibold text-teal">Batch #{cvBatch.id}</h3>
              <p className="text-sm text-textLight/60 capitalize">{cvBatch.status.replace(/_/g, " ")}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <SummaryCard label="Total" value={cvBatch.summary.total} />
              <SummaryCard label="Review" value={cvBatch.summary.ready_for_review} tone="teal" />
              <SummaryCard label="Failed" value={cvBatch.summary.parse_failed + cvBatch.summary.approval_failed} tone="gold" />
              <SummaryCard label="Approved" value={cvBatch.summary.approved} tone="tealLight" />
            </div>

            <div className="space-y-2">
              {cvBatch.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveItemId(item.id)}
                  className={`w-full text-left rounded-lg border p-3 transition-all ${
                    activeItemId === item.id ? "border-teal bg-teal/10" : "border-teal/20 bg-navy hover:border-teal/60"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium text-textLight">{item.original_filename}</span>
                    <span className="text-xs uppercase text-gold">{item.status.replace(/_/g, " ")}</span>
                  </div>
                  {item.parser_errors?.length > 0 && (
                    <p className="mt-1 truncate text-xs text-textLight/60">{item.parser_errors[0]}</p>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-navy-light border border-teal/30 rounded-lg p-6 shadow-lg">
            {activeItem && activeItemStatuses.includes(activeItem.status) ? (
              <div className="space-y-5">
                <div>
                  <h3 className="font-heading text-xl font-semibold text-teal">Review Candidate</h3>
                  <p className="text-sm text-textLight/60">{activeItem.original_filename}</p>
                </div>

                {duplicateWarning && (
                  <div className="rounded-lg border border-gold/50 bg-gold/20 p-4 text-gold">
                    Candidate already exists: {duplicateWarning.masked
                      ? duplicateWarning.candidate_reference || duplicateWarning.name
                      : `${duplicateWarning.name || ""}${duplicateWarning.phone ? ` (${duplicateWarning.phone})` : ""}`}
                  </div>
                )}

                <ReviewInput label="Full Name" required value={reviewForm.name} error={reviewErrors.name} onChange={(value) => updateReviewField("name", value)} />
                <ReviewInput label="Mobile Number" required value={reviewForm.phone} error={reviewErrors.phone} onChange={(value) => updateReviewField("phone", value)} />
                <ReviewInput label="Email Address" required value={reviewForm.email} error={reviewErrors.email} onChange={(value) => updateReviewField("email", value)} />
                <ReviewInput label="Skills" required value={reviewForm.skills} error={reviewErrors.skills} onChange={(value) => updateReviewField("skills", value)} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ReviewInput label="Current Role" value={reviewForm.role} onChange={(value) => updateReviewField("role", value)} />
                  <ReviewInput label="Experience" value={reviewForm.experience} error={reviewErrors.experience} onChange={(value) => updateReviewField("experience", value)} />
                  <ReviewInput label="Current City" value={reviewForm.current_city} onChange={(value) => updateReviewField("current_city", value)} />
                  <ReviewInput label="Source" value={reviewForm.source} onChange={(value) => updateReviewField("source", value)} />
                </div>
                <ReviewTextarea label="Notes" value={reviewForm.notes} onChange={(value) => updateReviewField("notes", value)} />

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    disabled={approving || !!duplicateWarning}
                    onClick={handleApprove}
                    className="flex-1 inline-flex items-center justify-center rounded-lg bg-teal px-5 py-3 font-semibold text-white hover:bg-teal-dark disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {approving ? <Loader2 size={20} className="mr-2 animate-spin" /> : <CheckCircle size={20} className="mr-2" />}
                    Approve Candidate
                  </button>
                  <button
                    type="button"
                    onClick={handleSkip}
                    className="inline-flex items-center justify-center rounded-lg bg-navy px-5 py-3 font-semibold text-textLight border border-teal/30 hover:border-gold"
                  >
                    <SkipForward size={20} className="mr-2" />
                    Skip
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex min-h-[360px] flex-col items-center justify-center text-center text-textLight/70">
                <FileText size={48} className="mb-3 text-teal" />
                <p className="font-medium">Select a review-ready CV item.</p>
                <p className="mt-1 text-sm">Failed, approved, skipped, and expired items cannot be approved.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-heading font-bold text-gold">Bulk Upload</h1>
        <p className="text-textLight/70 mt-1">Upload candidate data or prepare CVs for recruiter review</p>
      </div>

      <div className="inline-flex rounded-lg border border-teal/30 bg-navy-light p-1">
        <button
          type="button"
          onClick={() => setMode("data")}
          className={`rounded-md px-4 py-2 text-sm font-semibold ${mode === "data" ? "bg-teal text-white" : "text-textLight/70 hover:text-teal"}`}
        >
          Candidate Data Upload
        </button>
        <button
          type="button"
          onClick={() => setMode("cv")}
          className={`rounded-md px-4 py-2 text-sm font-semibold ${mode === "cv" ? "bg-teal text-white" : "text-textLight/70 hover:text-teal"}`}
        >
          Bulk CV Upload
        </button>
      </div>

      {mode === "data" ? renderDataUpload() : renderCvUpload()}
    </div>
  );
}

function SummaryCard({ label, value, tone = "default" }) {
  const color = tone === "teal" ? "text-teal" : tone === "tealLight" ? "text-teal-light" : tone === "gold" ? "text-gold" : "text-textLight";
  return (
    <div className="bg-navy rounded-lg p-4 border border-teal/20">
      <p className="text-sm text-textLight/70">{label}</p>
      <p className={`text-2xl font-heading font-bold ${color}`}>{value ?? 0}</p>
    </div>
  );
}

function ReviewInput({ label, value, onChange, error, required = false }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-textLight">
        {label} {required && <span className="text-gold">*</span>}
      </span>
      <input
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        className={`w-full rounded-lg border bg-navy px-3 py-2 text-textLight focus:outline-none focus:ring-2 focus:ring-teal ${error ? "border-gold" : "border-teal/30"}`}
      />
      {error && <span className="mt-1 block text-xs text-gold">{error}</span>}
    </label>
  );
}

function ReviewTextarea({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-textLight">{label}</span>
      <textarea
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        rows={3}
        className="w-full rounded-lg border border-teal/30 bg-navy px-3 py-2 text-textLight focus:outline-none focus:ring-2 focus:ring-teal"
      />
    </label>
  );
}

export default UploadPage;
